import React, { useEffect, useState } from "react";
import api from "../api/api";
import html2pdf from "html2pdf.js";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Button,
} from "@mui/material";
import logo from "./messiahnewlogo.jpg"; // local logo

const AcademicReport = () => {
  const [gradeClasses, setGradeClasses] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [selectedGradeClass, setSelectedGradeClass] = useState("");
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchGradeClasses = async () => {
      try {
        const res = await api.get("/api/users/grade-classes/");
        setGradeClasses(res.data);
      } catch (err) {
        console.error(err);
      }
    };

    const fetchPeriods = async () => {
      try {
        const res = await api.get("/api/users/periods/");
        setPeriods(res.data);
      } catch (err) {
        console.error(err);
      }
    };

    fetchGradeClasses();
    fetchPeriods();
  }, []);

  useEffect(() => {
    const fetchReport = async () => {
      if (!selectedGradeClass || !selectedPeriod) return;
      setLoading(true);
      try {
        const res = await api.get(
          `/academic-report/${selectedGradeClass}/${selectedPeriod}/`
        );
        setReportData(res.data);
      } catch (err) {
        console.error(err);
        setReportData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [selectedGradeClass, selectedPeriod]);

  const getRemarkColor = (remark) => {
    if (!remark) return "black";
    if (remark.includes("Principal")) return "blue";
    if (remark.includes("High Honor")) return "#87CEFA";
    if (remark.includes("Honor Roll")) return "#D3D3D3";
    if (remark.includes("Needs Improvement")) return "#FF6961";
    return "black";
  };

  const generatePDF = async (dataArray, fileName) => {
    if (!dataArray) return;
    const container = document.createElement("div");
    container.style.backgroundColor = "#fdfdfd";

    const dataList = Array.isArray(dataArray) ? dataArray : [dataArray];

    dataList.forEach((data) => {
      const page = document.createElement("div");
      page.style.padding = "10px";
      page.style.position = "relative";
      page.style.pageBreakAfter = "always";

      // Header
      const header = document.createElement("div");
      header.style.display = "flex";
      header.style.alignItems = "center";
      header.style.justifyContent = "space-between";
      header.style.marginBottom = "20px";

      const img = document.createElement("img");
      img.src = logo;
      img.style.height = "60px";

      const titleBox = document.createElement("div");
      const title = document.createElement("h2");
      title.innerText = `Academic Report - ${data.grade_class}`;
      title.style.margin = "0";
      const subTitle = document.createElement("p");
      subTitle.innerText = `Period: ${data.period}\nMessiah Mission Institute`;
      subTitle.style.margin = "0";
      subTitle.style.fontSize = "12px";
      titleBox.appendChild(title);
      titleBox.appendChild(subTitle);

      header.appendChild(img);
      header.appendChild(titleBox);
      page.appendChild(header);

      // Top Student
      if (data.top_honor_student) {
        const topDiv = document.createElement("div");
        topDiv.style.backgroundColor = "#b4e0fdff";
        topDiv.style.padding = "5px";
        topDiv.style.marginBottom = "10px";

        const topTitle = document.createElement("h3");
        topTitle.innerText = "Top Student This Period";
        const topText = document.createElement("p");
        topText.innerText = `${data.top_honor_student.student_name} � Average: ${data.top_honor_student.average} | Rank: ${data.top_honor_student.rank}`;

        topDiv.appendChild(topTitle);
        topDiv.appendChild(topText);
        page.appendChild(topDiv);
      }

      // Function to create tables
      const createTable = (sectionData, sectionName, includeRank = true) => {
        if (!sectionData || sectionData.length === 0) return;

        const secTitle = document.createElement("h3");
        secTitle.innerText = sectionName;
        secTitle.style.color = "#333";
        page.appendChild(secTitle);

        const table = document.createElement("table");
        table.style.width = "100%";
        table.style.borderCollapse = "collapse";
        table.style.marginBottom = "15px";
        table.style.pageBreakInside = "avoid";

        const thead = document.createElement("thead");
        const trHead = document.createElement("tr");
        const headers = includeRank ? ["#", "Name", "Average", "Remark"] : ["Name", "Average", "Remark"];
        headers.forEach((text) => {
          const th = document.createElement("th");
          th.innerText = text;
          th.style.border = "1px solid #999";
          th.style.padding = "5px";
          th.style.backgroundColor = includeRank ? "#E0F7FA" : "#FFE0E0";
          trHead.appendChild(th);
        });
        thead.appendChild(trHead);
        table.appendChild(thead);

        const tbody = document.createElement("tbody");
        sectionData.forEach((s, idx) => {
          const tr = document.createElement("tr");
          const cells = includeRank ? [s.rank || idx + 1, s.student_name, s.average, s.remark] : [s.student_name, s.average, s.remark];
          cells.forEach((val, i) => {
            const td = document.createElement("td");
            td.innerText = val;
            td.style.border = "1px solid #999";
            td.style.padding = "5px";
            if (i === (includeRank ? 3 : 2)) td.style.color = getRemarkColor(val);
            tr.appendChild(td);
          });
          tbody.appendChild(tr);
        });
        table.appendChild(tbody);
        page.appendChild(table);
      };

      createTable(data.honor_students, "Honor Students");
      createTable(data.delinquent_students, "Delinquent Students", false);

      if (data.honor_groups) {
        Object.entries(data.honor_groups).forEach(([groupName, students]) => {
          createTable(students, `${groupName} (${students.length})`);
        });
      }

      // Totals
      const totalsDiv = document.createElement("div");
      totalsDiv.style.marginTop = "10px";
      totalsDiv.innerHTML = `
        <p>Total Honor Students: <strong>${data.total_honor_students}</strong></p>
        <p>Total Delinquent Students: <strong>${data.total_delinquent_students}</strong></p>
      `;
      page.appendChild(totalsDiv);

      // Footer fixed
const footer = document.createElement("div");
footer.innerText = "Polu Integrated Technology - School Management System - 0776320624/0888144104";
footer.style.fontSize = "10px";
footer.style.color = "#000";
footer.style.backgroundColor = "#D3D3D3";
footer.style.padding = "5px";
footer.style.textAlign = "center";
footer.style.marginTop = "20px"; // add space above footer
page.appendChild(footer);
container.appendChild(page);
    });

    const opt = {
      margin: 10,
      filename: `${fileName}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
      pagebreak: { mode: ["css", "legacy"] },
    };

    html2pdf().set(opt).from(container).save();
  };

  const downloadSelected = () =>
    generatePDF(reportData, `AcademicReport_${selectedGradeClass}_${selectedPeriod}`);

  const downloadAllPeriod = async () => {
    if (!selectedPeriod) return;
    try {
      const res = await api.get(`/academic-report/all/${selectedPeriod}/`);
      generatePDF(res.data, `AcademicReport_All_${selectedPeriod}`);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, color: "black" }}>
        Academic Report
      </Typography>

      {/* Grade Class & Period selectors */}
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Grade Class</InputLabel>
          <Select value={selectedGradeClass} onChange={(e) => setSelectedGradeClass(e.target.value)}>
            {gradeClasses.map((gc) => (
              <MenuItem key={gc.id} value={gc.id}>{gc.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Period</InputLabel>
          <Select value={selectedPeriod} onChange={(e) => setSelectedPeriod(e.target.value)}>
            {periods.map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* PDF Buttons */}
      <Box sx={{ mb: 3, display: "flex", gap: 2 }}>
        <Button variant="contained" color="primary" onClick={downloadSelected}>
          View & Download Selected Grade Class
        </Button>
        <Button variant="contained" color="secondary" onClick={downloadAllPeriod}>
          View & Download All Grade Classes for Period
        </Button>
      </Box>

      {/* Report Tables */}
      {loading ? (
        <Typography>Loading...</Typography>
      ) : reportData ? (
        <>
          <Typography variant="h6" sx={{ mb: 1 }}>Top Honor Student</Typography>
          {reportData.top_honor_student && (
            <Paper sx={{ p: 2, mb: 3, backgroundColor: "#FFF9C4" }}>
              <Typography variant="subtitle1">
                {reportData.top_honor_student.student_name} � Average: {reportData.top_honor_student.average} | Rank: {reportData.top_honor_student.rank}
              </Typography>
            </Paper>
          )}

          {/* Honor Students */}
          <Typography variant="h6" sx={{ mb: 1 }}>Honor Students</Typography>
          <Table component={Paper} sx={{ mb: 3 }}>
            <TableHead sx={{ backgroundColor: "#E0F7FA" }}>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Average</TableCell>
                <TableCell>Remark</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reportData.honor_students.map((s) => (
                <TableRow key={s.student_id}>
                  <TableCell>{s.rank}</TableCell>
                  <TableCell>{s.student_name}</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>{s.average}</TableCell>
                  <TableCell sx={{ color: getRemarkColor(s.remark) }}>{s.remark}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Delinquent Students */}
          <Typography variant="h6" sx={{ mb: 1 }}>Delinquent Students</Typography>
          <Table component={Paper} sx={{ mb: 3 }}>
            <TableHead sx={{ backgroundColor: "#FFE0E0" }}>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Average</TableCell>
                <TableCell>Remark</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reportData.delinquent_students.map((s) => (
                <TableRow key={s.student_id}>
                  <TableCell>{s.student_name}</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>{s.average}</TableCell>
                  <TableCell sx={{ color: getRemarkColor(s.remark) }}>{s.remark}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Honor Groups */}
          {reportData.honor_groups && Object.entries(reportData.honor_groups).map(([groupName, students]) => (
            <Box key={groupName} sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>{groupName} ({students.length})</Typography>
              <Table component={Paper}>
                <TableHead sx={{ backgroundColor: "#F0F0F0" }}>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Average</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {students.map((s) => (
                    <TableRow key={s.student_id}>
                      <TableCell>{s.rank}</TableCell>
                      <TableCell>{s.student_name}</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>{s.average}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ))}

          {/* Totals */}
          <Divider sx={{ my: 2 }} />
          <Typography>Total Honor Students: <strong>{reportData.total_honor_students}</strong></Typography>
          <Typography>Total Delinquent Students: <strong>{reportData.total_delinquent_students}</strong></Typography>
        </>
      ) : (
        <Typography>Select Grade Class and Period to view report.</Typography>
      )}
    </Box>
  );
};

export default AcademicReport;
